import React from 'react';
import { motion } from 'framer-motion';
import { MoreVertical, Mail, Phone, Calendar, Crown, Star } from 'lucide-react';
import { TUserItem } from '@/data/types';

interface UserGridCardProps {
  user: TUserItem;
  index: number;
  getUserInitials: (user: TUserItem) => string;
  getUserRole: (user: TUserItem) => string;
  getUserDepartment: (user: TUserItem) => string;
  getUserStatus: (user: TUserItem) => 'active' | 'inactive';
  getUserRating: (user: TUserItem) => number;
  getUserProjectsCount: (user: TUserItem) => number;
  getPermissionsCount: (user: TUserItem) => number;
  formatJoinDate: (dateString: string) => string;
  getStatusColor: (status: string) => string;
  getDepartmentColor: (department: string) => string;
}

const UserGridCard: React.FC<UserGridCardProps> = React.memo(({
  user,
  index,
  getUserInitials,
  getUserRole,
  getUserDepartment,
  getUserStatus,
  getUserRating,
  getUserProjectsCount,
  getPermissionsCount,
  formatJoinDate,
  getStatusColor,
  getDepartmentColor,
}) => {
  const initials = getUserInitials(user);
  const role = getUserRole(user);
  const department = getUserDepartment(user);
  const status = getUserStatus(user);
  const rating = getUserRating(user);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
    >
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {user.avatar || user.image_url ? (
            <img
              src={user.avatar || user.image_url || ''}
              alt={`${user.first_name} ${user.last_name}`}
              className="h-12 w-12 rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 font-semibold text-white ${user.avatar || user.image_url ? 'hidden' : 'flex'}`}
          >
            {initials}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {user.first_name} {user.last_name}
            </h3>
            <p className="text-sm text-gray-600">{role}</p>
          </div>
        </div>
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="rounded-lg p-1 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-100"
          >
            <MoreVertical className="h-5 w-5 text-gray-400" />
          </motion.button>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Mail className="h-4 w-4" />
          <span className="truncate">{user.email}</span>
        </div>
        {user.phone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone className="h-4 w-4" />
            <span>{user.phone}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4" />
          <span>Εγγραφή {formatJoinDate(user.created_at)}</span>
        </div>
        {user.roles?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Crown className="h-4 w-4" />
            <span>
              {user.roles.length} ρόλο{user.roles.length !== 1 ? 'ι' : 'ς'}
            </span>
          </div>
        )}
      </div>



      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getDepartmentColor(department)}`}>
          {department}
        </span>
        <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(status)}`}>
          {status === 'active' ? 'Ενεργός' : 'Ανενεργός'}
        </span>
      </div>

      {/* Permissions indicator */}
      {getPermissionsCount(user) > 0 && (
        <div className="mt-2 text-xs text-blue-600">{getPermissionsCount(user)} δικαιώματα</div>
      )}
    </motion.div>
  );
});

UserGridCard.displayName = 'UserGridCard';

export default UserGridCard;
